import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Snackbar
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useAuth } from '../App'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

interface ApiKeysModalProps {
  open: boolean
  onClose: () => void
}

interface ApiKey {
  id: string
  name: string
  key_suffix: string
  is_active: boolean
  created_at: string
  last_used_at: string | null
  metadata?: Record<string, any>
}

interface NewApiKeyResponse {
  id: string
  name: string
  key: string
  key_suffix: string
}

const ApiKeysModal: React.FC<ApiKeysModalProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [error, setError] = useState<string | null>(null)
  const [newKeyName, setNewKeyName] = useState('')
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false)
  const [newKeyResponse, setNewKeyResponse] = useState<NewApiKeyResponse | null>(null)
  const [showNewKey, setShowNewKey] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const auth = useAuth()

  useEffect(() => {
    if (open) {
      fetchApiKeys()
    }
  }, [open])

  const fetchApiKeys = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/api-keys`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setApiKeys(data.keys || [])
      } else {
        setError('Failed to load API keys')
      }
    } catch (err) {
      setError('Network error occurred')
      console.error('API keys fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a key name')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newKeyName,
          metadata: {
            created_via: 'developer_portal',
            environment: 'production'
          }
        })
      })

      if (response.ok) {
        const newKey = await response.json()
        setNewKeyResponse(newKey)
        setShowNewKeyDialog(true)
        setNewKeyName('')
        await fetchApiKeys() // Refresh the list
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to create API key')
      }
    } catch (err) {
      setError('Network error occurred')
      console.error('API key creation error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteApiKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        await fetchApiKeys() // Refresh the list
        setSnackbarMessage('API key deleted successfully')
        setSnackbarOpen(true)
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to delete API key')
      }
    } catch (err) {
      setError('Network error occurred')
      console.error('API key deletion error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRotateApiKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to rotate this API key? The old key will be deactivated.')) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/api-keys/${keyId}/rotate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const rotatedKey = await response.json()
        setNewKeyResponse(rotatedKey)
        setShowNewKeyDialog(true)
        await fetchApiKeys() // Refresh the list
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to rotate API key')
      }
    } catch (err) {
      setError('Network error occurred')
      console.error('API key rotation error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbarMessage('Copied to clipboard')
      setSnackbarOpen(true)
    })
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString()
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <VpnKeyIcon sx={{ mr: 1 }} />
              <Typography variant="h6">API Keys Management</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Create New API Key Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Create New API Key
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Key Name"
                    placeholder="e.g., Production API Key"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<AddIcon />}
                    onClick={handleCreateApiKey}
                    disabled={loading || !newKeyName.trim()}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Create Key'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* API Keys List */}
          <Typography variant="h6" gutterBottom>
            Your API Keys
          </Typography>
          
          {loading && apiKeys.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : apiKeys.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              No API keys found. Create your first API key above.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Key Suffix</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map((apiKey) => (
                    <TableRow key={apiKey.id}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {apiKey.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            ...{apiKey.key_suffix}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={apiKey.is_active ? 'Active' : 'Inactive'}
                          color={apiKey.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(apiKey.created_at)}
                      </TableCell>
                      <TableCell>
                        {formatDate(apiKey.last_used_at)}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <Tooltip title="Rotate Key">
                            <IconButton
                              size="small"
                              onClick={() => handleRotateApiKey(apiKey.id)}
                              disabled={loading}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Key">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteApiKey(apiKey.id)}
                              disabled={loading}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* New API Key Display Dialog */}
      <Dialog 
        open={showNewKeyDialog} 
        onClose={() => setShowNewKeyDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">New API Key Created</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              Important: This is the only time you'll see the complete API key. 
              Please copy it and store it securely.
            </Typography>
          </Alert>
          
          {newKeyResponse && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {newKeyResponse.name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="API Key"
                    type={showNewKey ? 'text' : 'password'}
                    value={newKeyResponse.key}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: 'monospace' }
                    }}
                  />
                  <IconButton onClick={() => setShowNewKey(!showNewKey)}>
                    {showNewKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                  <Tooltip title="Copy to clipboard">
                    <IconButton onClick={() => handleCopyToClipboard(newKeyResponse.key)}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Key ID: ...{newKeyResponse.key_suffix}
                </Typography>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewKeyDialog(false)} variant="contained">
            I've Saved the Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  )
}

export default ApiKeysModal